import { PaginationType } from '../../types/utils/pagination.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { BaseCDRListItemType } from '../../types/cdr/base-cdr.type';
import { createSlice } from '@reduxjs/toolkit';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import snackbarUtils from '../../utils/SnackbarUtils';
import { getATACDRsList, getCDRMonitorLink, getCDRsList } from './cdrs.action';
import { getSettings } from '../../utils/utilities';

const settings = getSettings();

export interface CDRsState {
  netsapiensCDRsRouteParams: RouteParamsType;
  megaSwitchCDRsRouteParams: RouteParamsType;
  csrpCDRsRouteParams: RouteParamsType;
  cdrsList: null | PaginationType<BaseCDRListItemType>;
  cdrsListFetchStatus: FETCH_STATUS;
  cdrMonitorLinkFetchStatus: FETCH_STATUS;
  ataCDRsRouteParams: RouteParamsType;
  ataCDRsList: null | PaginationType<BaseCDRListItemType>;
  ataCDRsListFetchStatus: FETCH_STATUS;
}

export const initialCDRsRouteParams = {
  page: 1,
  limit: settings?.limit || 10,
  order: '-cdr.timeStart',
  q: '',
  call_result: 'all',
  start_date: '',
  end_date: '',
};

export const initialCDRsWithSystemRouteParams = {
  ...initialCDRsRouteParams,
  esi_id: '',
};

export const initialATACDRsRouteParams = {
  page: 1,
  limit: settings?.limit || 10,
  order: '-cdr.timeStart',
  q: '',
  start_date: '',
  end_date: '',
};

const INIT_STATE: CDRsState = {
  netsapiensCDRsRouteParams: initialCDRsWithSystemRouteParams,
  megaSwitchCDRsRouteParams: initialCDRsWithSystemRouteParams,
  csrpCDRsRouteParams: initialCDRsWithSystemRouteParams,
  cdrsList: null,
  cdrsListFetchStatus: FETCH_STATUS.NULL,
  cdrMonitorLinkFetchStatus: FETCH_STATUS.NULL,
  ataCDRsRouteParams: initialATACDRsRouteParams,
  ataCDRsList: null,
  ataCDRsListFetchStatus: FETCH_STATUS.NULL,
};

export const cdrsSlice = createSlice({
  name: 'cdrs',
  initialState: INIT_STATE,
  reducers: {
    setCDRsList: (state, action) => {
      state.cdrsList = action.payload;
    },
    setCDRsListFetchStatus: (state, action) => {
      state.cdrsListFetchStatus = action.payload;
    },
    setATACDRsList: (state, action) => {
      state.ataCDRsList = action.payload;
    },
    setATACDRsListFetchStatus: (state, action) => {
      state.ataCDRsListFetchStatus = action.payload;
    },
    setCDRMonitorLinkFetchStatus: (state, action) => {
      state.cdrMonitorLinkFetchStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'netsapiens-cdrs') {
        state.netsapiensCDRsRouteParams = { ...state.netsapiensCDRsRouteParams, ...payload.data };
      }

      if (payload.reducer === 'megaswitch-cdrs') {
        state.megaSwitchCDRsRouteParams = { ...state.megaSwitchCDRsRouteParams, ...payload.data };
      }

      if (payload.reducer === 'csrp-cdrs') {
        state.csrpCDRsRouteParams = { ...state.csrpCDRsRouteParams, ...payload.data };
      }

      if (payload.reducer === 'ata-cdrs') {
        state.ataCDRsRouteParams = { ...state.ataCDRsRouteParams, ...payload.data };
      }
    });
    // GET CDRS LIST
    builder.addCase(getCDRsList.pending, state => {
      state.cdrsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getCDRsList.rejected, (state, action) => {
      if (action.payload) {
        state.cdrsListFetchStatus = FETCH_STATUS.REJECTED;
        // state.cdrsList = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getCDRsList.fulfilled, (state, action) => {
      state.cdrsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.cdrsList = action.payload;
    });
    // GET CDRS LIST
    builder.addCase(getATACDRsList.pending, state => {
      state.ataCDRsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getATACDRsList.rejected, (state, action) => {
      if (action.payload) {
        state.ataCDRsListFetchStatus = FETCH_STATUS.REJECTED;
        // state.ataCDRsList = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getATACDRsList.fulfilled, (state, action) => {
      state.ataCDRsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.ataCDRsList = action.payload;
    });

    // GET CDR MONITOR LINK
    builder.addCase(getCDRMonitorLink.pending, state => {
      state.cdrMonitorLinkFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getCDRMonitorLink.rejected, (state, action) => {
      state.cdrMonitorLinkFetchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getCDRMonitorLink.fulfilled, state => {
      state.cdrMonitorLinkFetchStatus = FETCH_STATUS.FULFILLED;
    });
  },
});

export const {
  setCDRsList,
  setCDRsListFetchStatus,
  setCDRMonitorLinkFetchStatus,
  setATACDRsListFetchStatus,
  setATACDRsList,
} = cdrsSlice.actions;
export default cdrsSlice.reducer;
